import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';
import Card3D from './Card3D';
import TimePeriodFilter from './TimePeriodFilter';

const ACCENT = '#8A74E4';
const USER_COLOR = '#ECE2DB'; // cream — the player's own color
const GM_COLORS = ['#00A5C4', '#DE7345', '#E4446E']; // blue, orange, pink — one per matched GM

const ARCHETYPE_IMAGES = {
  snake: '/Snake.png', blackpanther: '/BlackPanther.png', fox: '/Fox.png', dragon: '/Dragon.png',
  octopus: '/Octopus.png', elephant: '/Elephant.png', phoenix: '/Phoenix.png', stag: '/Stag.png',
  shade: '/Snake.png', reaper: '/BlackPanther.png', demon: '/Fox.png', inferno: '/Dragon.png',
  leviathan: '/Octopus.png', monolith: '/Elephant.png', guardian: '/Stag.png',
};

const SINGULAR_NAMES = { 'Snakes': 'Snake', 'Panthers': 'Panther', 'Foxes': 'Fox', 'Dragons': 'Dragon', 'Octopuses': 'Octopus', 'Elephants': 'Elephant', 'Phoenixes': 'Phoenix', 'Stags': 'Stag' };
const singularName = (name) => SINGULAR_NAMES[name] || name;


// ── Dimension bar ──
function DimBar({ leftLabel, rightLabel, value, color, c, desc }) { // eslint-disable-line no-unused-vars
  const pct = Math.round(value * 100);
  const isHigh = value > 0.5;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ color: !isHigh ? c.textDark : c.textMuted, fontSize: '1.05rem', fontWeight: !isHigh ? 700 : 500 }}>{leftLabel}</Typography>
        <Typography sx={{ color: isHigh ? c.textDark : c.textMuted, fontSize: '1.05rem', fontWeight: isHigh ? 700 : 500 }}>{rightLabel}</Typography>
      </Box>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: `${c.textMuted}10`, mt: 4, overflow: 'visible' }}>
        <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: `${c.textMuted}20`, zIndex: 1 }} />
        <Box sx={{
          position: 'absolute', top: 0, bottom: 0,
          ...(pct <= 50 ? { right: '50%', width: `${50 - pct}%` } : { left: '50%', width: `${pct - 50}%` }),
          bgcolor: color, borderRadius: 4, transition: 'width 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        {/* Dot + percentage at end of bar */}
        <Typography sx={{ color, fontSize: '1.25rem', fontWeight: 900, position: 'absolute', left: `${pct}%`, top: -38, transform: 'translateX(-50%)', zIndex: 3 }}>{Math.max(pct, 100 - pct)}%</Typography>
        <Box sx={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: '50%', bgcolor: color, border: `3px solid ${c.cardBg || c.bg}`, boxShadow: `0 0 10px ${color}60`, zIndex: 2 }} />
      </Box>
      <Box sx={{ mt: 0.8 }}>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontStyle: 'italic' }}>{desc}</Typography>
      </Box>
    </Box>
  );
}

// ── Metric definitions grouped by dimension ──
const DIMENSION_METRICS = {
  stance: {
    label: 'Stance',
    left: 'Positional',
    right: 'Aggressive',
    desc: 'Do you grind opponents down or go for the kill?',
    metrics: [
      { key: 'captures_per_game', label: 'Captures / Game', desc: 'How many pieces you take per game on average', format: (v) => v?.toFixed(1), weight: 2.0, direction: 'high=aggressive' },
      { key: 'checks_per_game', label: 'Checks / Game', desc: 'How often you put the opponent king in check', format: (v) => v?.toFixed(2), weight: 1.5, direction: 'high=aggressive' },
      { key: 'queen_deploy_timing', label: 'Queen Deploy (move)', desc: 'How early you bring your queen into the game', format: (v) => v?.toFixed(1), weight: 1.5, direction: 'low=aggressive' },
      { key: 'pawn_push_depth', label: 'Pawn Push Depth', desc: 'How far you advance your pawns on average', format: (v) => v?.toFixed(2), weight: 1.5, direction: 'high=aggressive' },
      { key: 'trade_initiation_rate', label: 'Trade Initiation', desc: 'How often you are the one initiating piece trades', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.0, direction: 'high=aggressive' },
      { key: 'quiet_move_ratio', label: 'Quiet Move Ratio', desc: 'Proportion of non-capturing, non-checking moves', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'high=positional' },
      { key: 'opposite_side_castling_rate', label: 'Opposite Castling', desc: 'How often you castle on the opposite side of your opponent', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.0, direction: 'high=aggressive' },
      { key: 'first_blood_timing', label: 'First Capture (move)', desc: 'The average move number of your first piece capture', format: (v) => v?.toFixed(1), weight: 1.0, direction: 'low=aggressive' },
      { key: 'center_control_per_game', label: 'Center Control', desc: 'How often you place pieces on the 4 central squares', format: (v) => v?.toFixed(1), weight: 1.5, direction: 'high=aggressive' },
      { key: 'development_speed', label: 'Development Speed', desc: 'How many minor pieces you develop in the first 10 moves', format: (v) => v?.toFixed(1), weight: 1.0, direction: 'high=aggressive' },
    ],
  },
  board: {
    label: 'Board',
    left: 'Cleared',
    right: 'Dense',
    desc: 'Do you simplify positions or thrive in chaos?',
    metrics: [
      { key: 'trades_per_game', label: 'Trades / Game', desc: 'Total piece exchanges per game', format: (v) => v?.toFixed(1), weight: 2.0, direction: 'low=wild' },
      { key: 'simplification_tendency', label: 'Simplification', desc: 'How quickly you reduce material on the board', format: (v) => v?.toFixed(1), weight: 2.0, direction: 'low=wild' },
      { key: 'endgame_reach_rate', label: 'Endgame Reach', desc: 'How often your games make it to an endgame', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'low=wild' },
      { key: 'pawn_breaks_per_game', label: 'Pawn Breaks / Game', desc: 'Pawn advances that open up the position', format: (v) => v?.toFixed(2), weight: 1.0, direction: 'high=wild' },
      { key: 'pawn_structure_score', label: 'Pawn Structure', desc: 'How clean and organized your pawn formations are', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'low=wild' },
      { key: 'material_imbalance_tolerance', label: 'Material Imbalance', desc: 'How comfortable you are playing with unequal material', format: (v) => v?.toFixed(2), weight: 1.5, direction: 'high=wild' },
      { key: 'king_pawn_shield', label: 'King Pawn Shield', desc: 'How well you maintain pawns in front of your king', format: (v) => v?.toFixed(1), weight: 1.0, direction: 'low=wild' },
      { key: 'recapture_rate', label: 'Recapture Rate', desc: 'How often you immediately recapture after losing a piece', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'low=wild' },
      { key: 'piece_centralization', label: 'Piece Spread', desc: 'How far your pieces sit from the center of the board', format: (v) => v?.toFixed(2), weight: 1.0, direction: 'high=wild' },
    ],
  },
  pulse: {
    label: 'Pulse',
    left: 'Intense',
    right: 'Stoic',
    desc: 'Do you ride emotions or stay ice-cold?',
    metrics: [
      { key: 'conversion_rate', label: 'Conversion Rate', desc: 'How often you win games where you had an advantage', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 2.0, direction: 'high=stoic' },
      { key: 'comeback_rate', label: 'Comeback Rate', desc: 'How often you recover from losing positions', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'high=passionate' },
      { key: 'resignation_timing', label: 'Resignation Timing', desc: 'How long you play on in lost games relative to average', format: (v) => v?.toFixed(2), weight: 2.0, direction: 'high=passionate' },
      { key: 'draw_repetition_rate', label: 'Draw by Repetition', desc: 'How often your draws come from position repetition', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'high=stoic' },
      { key: 'move_repetition_rate', label: 'Piece Shuffling', desc: 'How often you move a piece back and forth', format: (v) => `${(v * 100)?.toFixed(1)}%`, weight: 1.5, direction: 'high=passionate' },
      { key: 'castling_rate', label: 'Castling Rate', desc: 'How consistently you castle across your games', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 2.0, direction: 'high=stoic' },
      { key: 'move_time_consistency', label: 'Time Consistency', desc: 'How steady your thinking time is across moves', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 2.0, direction: 'high=stoic' },
      { key: 'time_trouble_rate', label: 'Time Trouble', desc: 'How often you drop below 10% of your clock', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.5, direction: 'low=stoic' },
      { key: 'fast_move_pct', label: 'Fast Moves', desc: 'Proportion of moves made in under 2 seconds', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 1.0, direction: 'low=stoic' },
      { key: 'think_burst_rate', label: 'Think Bursts', desc: 'Moves where you spent 3x longer than your average', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 0.5, direction: 'low=stoic' },
    ],
  },
  arsenal: {
    label: 'Arsenal',
    left: 'Specialized',
    right: 'Diverse',
    desc: 'Do you master a few openings or play everything?',
    metrics: [
      { key: 'opening_entropy', label: 'Opening Entropy', desc: 'How evenly spread your opening choices are across your repertoire', format: (v) => `${(v * 100)?.toFixed(0)}%`, weight: 3.0, direction: 'high=diverse' },
      { key: 'piece_usage_entropy', label: 'Piece Usage Entropy', desc: 'How evenly you use all your pieces', format: (v) => v?.toFixed(2), weight: 2.0, direction: 'high=diverse' },
      { key: 'game_length_variance', label: 'Game Length Variance', desc: 'How much your game lengths vary from game to game', format: (v) => v?.toFixed(2), weight: 1.5, direction: 'high=diverse' },
      { key: 'color_preference_delta', label: 'Color Preference Gap', desc: 'Difference in your play style between White and Black', format: (v) => v?.toFixed(3), weight: 1.0, direction: 'low=diverse' },
      { key: 'opening_depth', label: 'Opening Depth', desc: 'How many book moves deep you play on average before deviating', format: (v) => v?.toFixed(1), weight: 1.5, direction: 'high=specialized' },
    ],
  },
};

// ── Archetype descriptions for the info section ──
const ARCHETYPE_INFO = {
  snake: {
    emoji: '', trait: 'Calculated aggression in complex positions',
    playstyle: 'You move silently through chaotic positions, striking with precision when the opponent least expects it. Your aggression is hidden behind layers of complexity.',
    rarity: 12,
    strengths: ['Thrives in chaotic middlegames', 'Excellent tactical vision under pressure', 'Opponents can\'t predict your attacks'],
    weaknesses: ['Can overextend in quiet positions', 'Simplified endgames neutralize your edge', 'Impatience when no tactics are available'],
    famousPlayers: ['Sergey Karjakin', 'Paul Morphy', 'Andrew Tang'],
    matchups: {
      dominates: [
        { key: 'dragon', reason: 'Your complex chaos denies Dragon the clean strike they need.' },
        { key: 'elephant', reason: 'Your aggressive complications prevent Elephant\'s forced simplification.' },
      ],
      strugglesVs: [
        { key: 'phoenix', reason: 'Phoenix fights back through your chaos and counter-punches when you tire.' },
        { key: 'blackpanther', reason: 'Panther\'s clinical strikes cut through your setups before they form.' },
      ],
    },
  },
  blackpanther: {
    emoji: '', trait: 'Clean, decisive tactical execution',
    playstyle: 'You simplify with surgical precision and strike once with lethal accuracy. Your games are short, decisive, and brutally efficient.',
    rarity: 10,
    strengths: ['Clinical converting of advantages', 'Strong opening preparation', 'Rarely makes unnecessary moves'],
    weaknesses: ['Struggles when forced into chaos', 'Can be too eager to simplify', 'Passive when behind on material'],
    famousPlayers: ['Srinath Narayanan', 'Alice Lee', 'Swapnil Dhopade'],
    matchups: {
      dominates: [
        { key: 'octopus', reason: 'Your clinical strike cuts through Octopus\'s slow positional plan.' },
        { key: 'snake', reason: 'Your clean clarity forces Snake out of the complex setups they need.' },
      ],
      strugglesVs: [
        { key: 'stag', reason: 'Stag\'s relentless defense outlasts your single-strike attacks.' },
        { key: 'fox', reason: 'Fox\'s chaos disrupts your clean strikes before they can land.' },
      ],
    },
  },
  fox: {
    emoji: '', trait: 'Thriving in chaos with emotional fire',
    playstyle: 'You love wild, unpredictable positions and feed off the energy of complex middlegames. Chaos is your playground.',
    rarity: 10,
    strengths: ['Incredible resourcefulness in bad positions', 'Unpredictable and hard to prepare against', 'Feeds off opponent\'s time pressure'],
    weaknesses: ['Emotional swings affect consistency', 'Can create chaos when calm play is better', 'Blunder-prone in winning positions'],
    famousPlayers: ['Levon Aronian', 'Alexei Shirov', 'Alexander Alekhine'],
    matchups: {
      dominates: [
        { key: 'blackpanther', reason: 'Your emotional chaos overwhelms Panther\'s clean clinical approach.' },
        { key: 'stag', reason: 'Your overwhelming mess breaks through Stag\'s disciplined defense.' },
      ],
      strugglesVs: [
        { key: 'elephant', reason: 'Elephant simplifies away the chaos you need to thrive.' },
        { key: 'phoenix', reason: 'Phoenix survives your storms and counter-punches when you tire.' },
      ],
    },
  },
  dragon: {
    emoji: '', trait: 'Explosive bursts in simplified positions',
    playstyle: 'You attack with explosive passion, then force the game to a close. The game has one climax, one decisive moment, then it\'s over.',
    rarity: 16,
    strengths: ['Devastating initiative when attacking', 'Excellent at converting one chance', 'Strong in sharp, theoretical lines'],
    weaknesses: ['Struggles in slow, maneuvering games', 'Can burn out emotionally in long events', 'Over-commits to attacks that aren\'t there'],
    famousPlayers: ['Alireza Firouzja', 'Fabiano Caruana', 'Praggnanandhaa'],
    matchups: {
      dominates: [
        { key: 'elephant', reason: 'Your explosive attack lands before Elephant can trade into the endgame.' },
        { key: 'phoenix', reason: 'Your decisive strike hits before Phoenix\'s resilience kicks in.' },
      ],
      strugglesVs: [
        { key: 'octopus', reason: 'Octopus keeps the board dense — no clean targets for your attack.' },
        { key: 'snake', reason: 'Snake\'s complex positions deny the clean attacking lanes you need.' },
      ],
    },
  },
  octopus: {
    emoji: '', trait: 'Positional mastery in complex positions',
    playstyle: 'You wrap your tentacles around the position slowly, controlling every square. In complex positions, your calm precision is suffocating.',
    rarity: 14,
    strengths: ['Exceptional strategic understanding', 'Wins games without visible tactics', 'Dominant in long, complex middlegames'],
    weaknesses: ['Can be too passive against sharp attacks', 'Misses tactical shots in time trouble', 'Slow development can be punished'],
    famousPlayers: ['Bobby Fischer', 'Garry Kasparov', 'Emanuel Lasker'],
    matchups: {
      dominates: [
        { key: 'stag', reason: 'You build complexity Stag\'s clean defense can\'t contain.' },
        { key: 'dragon', reason: 'You keep pieces on the board, denying Dragon the clean attacking targets.' },
      ],
      strugglesVs: [
        { key: 'elephant', reason: 'Elephant\'s forced trades collapse the complexity you need.' },
        { key: 'blackpanther', reason: 'Panther\'s clinical strike cuts through your slow plan before it lands.' },
      ],
    },
  },
  elephant: {
    emoji: '', trait: 'Immovable positional grinding',
    playstyle: 'You build unbreakable structures and grind opponents down with patience. Slow, inevitable, unstoppable.',
    rarity: 14,
    strengths: ['Nearly impossible to beat in endgames', 'Rock-solid pawn structures', 'Wears down impatient opponents'],
    weaknesses: ['Can draw too many winning positions', 'Struggles against dynamic sacrifices', 'Difficulty creating winning chances from equal positions'],
    famousPlayers: ['Jose Raul Capablanca', 'Vassily Ivanchuk', 'Gata Kamsky'],
    matchups: {
      dominates: [
        { key: 'fox', reason: 'Your early trades strip away the chaos Foxes thrive in before it can form.' },
        { key: 'octopus', reason: 'You force piece trades that collapse Octopus\'s dense positional pressure.' },
      ],
      strugglesVs: [
        { key: 'dragon', reason: 'Dragon\'s explosive strike lands before you can trade into the endgame.' },
        { key: 'snake', reason: 'Snake\'s aggressive complications deny you the simple positions you need.' },
      ],
    },
  },
  phoenix: {
    emoji: '', trait: 'Positional resilience with emotional intensity',
    playstyle: 'You thrive in messy positions and never give up. When things look lost, you rise from the ashes with renewed determination.',
    rarity: 15,
    strengths: ['Incredible comeback ability', 'Thrives under pressure', 'Opponents can never relax against you'],
    weaknesses: ['Gets into trouble by relying on resilience', 'Inconsistent — brilliant one day, shaky the next', 'Can waste energy on lost causes'],
    famousPlayers: ['Magnus Carlsen', 'Ian Nepomniachtchi', 'Mikhail Botvinnik'],
    matchups: {
      dominates: [
        { key: 'snake', reason: 'Your resilience survives Snake\'s calculated chaos and punches back.' },
        { key: 'fox', reason: 'You outlast Fox\'s emotional storms and win when they\'ve burned out.' },
      ],
      strugglesVs: [
        { key: 'stag', reason: 'Stag\'s discipline punishes every one of your over-extensions.' },
        { key: 'dragon', reason: 'Dragon\'s explosive strike lands before your resilience activates.' },
      ],
    },
  },
  stag: {
    emoji: '', trait: 'Disciplined defense with passionate determination',
    playstyle: 'You hold clean positions with fire and stubbornness. You simplify early then defend the endgame like it\'s personal.',
    rarity: 9,
    strengths: ['Excellent endgame technique', 'Disciplined and hard to crack', 'Consistent results across tournaments'],
    weaknesses: ['Can be too defensive in dynamic positions', 'Misses attacking opportunities', 'Predictable style allows opponent preparation'],
    famousPlayers: ['Hikaru Nakamura', 'Anatoly Karpov', 'Tigran Petrosian'],
    matchups: {
      dominates: [
        { key: 'phoenix', reason: 'Your clean defensive discipline punishes Phoenix\'s over-extensions.' },
        { key: 'blackpanther', reason: 'You outlast Panther\'s single-strike attack and grind to victory.' },
      ],
      strugglesVs: [
        { key: 'octopus', reason: 'Octopus outmaneuvers you in complex middlegames you can\'t simplify.' },
        { key: 'fox', reason: 'Fox\'s overwhelming chaos breaks through even your best defense.' },
      ],
    },
  },
};

// ── Archetype names lookup (for matchup chips) ──
const ARCHETYPE_NAMES = {
  snake: 'Snake', blackpanther: 'Panther', fox: 'Fox', dragon: 'Dragon',
  octopus: 'Octopus', elephant: 'Elephant', phoenix: 'Phoenix', stag: 'Stag',
};

// ── GM → flag emoji lookup ──
const GM_FLAGS = {
  'Mikhail Tal': '🇱🇻',
  'Anatoly Karpov': '🇷🇺',
  'Bobby Fischer': '🇺🇸',
  'Magnus Carlsen': '🇳🇴',
  'Alexander Alekhine': '🇷🇺',
  'Jose Raul Capablanca': '🇨🇺',
  'José Raúl Capablanca': '🇨🇺',
  'Garry Kasparov': '🇷🇺',
  'Tigran Petrosian': '🇦🇲',
  'Sergey Karjakin': '🇷🇺',
  'Paul Morphy': '🇺🇸',
  'Andrew Tang': '🇺🇸',
  'Srinath Narayanan': '🇮🇳',
  'Alice Lee': '🇺🇸',
  'Swapnil Dhopade': '🇮🇳',
  'Levon Aronian': '🇦🇲',
  'Alexei Shirov': '🇱🇻',
  'Alireza Firouzja': '🇫🇷',
  'Fabiano Caruana': '🇺🇸',
  'Praggnanandhaa': '🇮🇳',
  'Rameshbabu Praggnanandhaa': '🇮🇳',
  'Emanuel Lasker': '🇩🇪',
  'Vassily Ivanchuk': '🇺🇦',
  'Gata Kamsky': '🇺🇸',
  'Ian Nepomniachtchi': '🇷🇺',
  'Mikhail Botvinnik': '🇷🇺',
  'Hikaru Nakamura': '🇺🇸',
  'Ding Liren': '🇨🇳',
  'Viswanathan Anand': '🇮🇳',
  'Dommaraju Gukesh': '🇮🇳',
  'Anish Giri': '🇳🇱',
  'Vladimir Kramnik': '🇷🇺',
  'Maxime Vachier-Lagrave': '🇫🇷',
  'Alexander Grischuk': '🇷🇺',
  'Peter Svidler': '🇷🇺',
  'Teimour Radjabov': '🇦🇿',
  'Vladislav Artemiev': '🇷🇺',
  'Alexander Morozevich': '🇷🇺',
  'Vidit Gujrathi': '🇮🇳',
  'Nihal Sarin': '🇮🇳',
  'Vincent Keymer': '🇩🇪',
  'Jan-Krzysztof Duda': '🇵🇱',
  'Levy Rozman': '🇺🇸',
  'Eric Hansen': '🇨🇦',
};
const gmFlag = (name) => {
  if (!name) return '';
  if (GM_FLAGS[name]) return GM_FLAGS[name];
  const stripped = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return GM_FLAGS[stripped] || '';
};

// ── Baseline population averages for hero-stat context ──
const BASELINE_METRICS = {
  endgame_reach_rate: 0.47,
  quiet_move_ratio: 0.45,
  resignation_timing: 1.0,
  comeback_rate: 0.22,
  checks_per_game: 1.2,
  material_imbalance_tolerance: 0.13,
  trade_initiation_rate: 0.50,
  castling_rate: 0.82,
};

// ── Archetype → signature hero stat config ──
const HERO_STAT_CONFIG = {
  elephant: { metricKey: 'endgame_reach_rate', label: 'Endgame reach rate', format: (v) => `${Math.round((v || 0) * 100)}%` },
  octopus: { metricKey: 'quiet_move_ratio', label: 'Quiet move ratio', format: (v) => `${Math.round((v || 0) * 100)}%` },
  stag: { metricKey: 'resignation_timing', label: 'Resistance in lost games', format: (v) => `${(v || 1).toFixed(1)}×` },
  phoenix: { metricKey: 'comeback_rate', label: 'Comeback rate', format: (v) => `${Math.round((v || 0) * 100)}%` },
  dragon: { metricKey: 'checks_per_game', label: 'Checks per game', format: (v) => `${(v || 0).toFixed(1)}×` },
  fox: { metricKey: 'material_imbalance_tolerance', label: 'Material imbalance tolerance', format: (v) => `${Math.round((v || 0) * 100)}%` },
  blackpanther: { metricKey: 'trade_initiation_rate', label: 'Trade initiation rate', format: (v) => `${Math.round((v || 0) * 100)}%` },
  snake: { metricKey: 'castling_rate', label: 'Castling rate', format: (v) => `${Math.round((v || 0) * 100)}%` },
};

function getHeroContext(metricKey, val) {
  const avg = BASELINE_METRICS[metricKey];
  if (avg == null || val == null) return null;
  const ratio = val / avg;
  if (ratio >= 1.25) return `${ratio.toFixed(1)}× the average player`;
  if (ratio >= 1.08) return `${Math.round((ratio - 1) * 100)}% above average`;
  if (ratio >= 0.92) return 'Right around average';
  if (ratio >= 0.75) return `${Math.round((1 - ratio) * 100)}% below average`;
  return `${ratio.toFixed(1)}× the average`;
}

function buildFingerprint(profileKey, m) {
  if (!m) return null;
  const r = Math.round;
  const pct = (v) => r((v || 0) * 100);
  const fx = (v, d) => (v != null ? Number(v).toFixed(d) : '—');
  switch (profileKey) {
    case 'elephant':
      return `You castle by move ${r(m.castling_timing || 8)}, initiate ${pct(m.trade_initiation_rate)}% of trades, and reach the endgame in ${pct(m.endgame_reach_rate)}% of games.`;
    case 'octopus':
      return `You keep ${pct(m.quiet_move_ratio)}% of your moves quiet and positional, trade only ${fx(m.trades_per_game, 1)} pieces per game, and delay the queen until move ${r(m.queen_deploy_timing || 15)}.`;
    case 'stag':
      return `You play on ${fx(m.resignation_timing, 1)}× longer than average before resigning, convert ${pct(m.conversion_rate)}% of winning positions, and castle in ${pct(m.castling_rate)}% of games.`;
    case 'phoenix':
      return `You win ${pct(m.comeback_rate)}% of games from losing positions, tolerate material imbalance in ${pct(m.material_imbalance_tolerance)}% of games, and resist resignation ${fx(m.resignation_timing, 1)}× longer than average.`;
    case 'dragon':
      return `You check the opposing king ${fx(m.checks_per_game, 1)}× per game, blitz ${pct(m.fast_move_pct)}% of your moves, and deploy the queen by move ${r(m.queen_deploy_timing || 10)}.`;
    case 'fox':
      return `You play ${pct(m.material_imbalance_tolerance)}% of games down material, push ${fx(m.pawn_breaks_per_game, 1)} pawn breaks per game, and castle in only ${pct(m.castling_rate)}% of games.`;
    case 'blackpanther':
      return `You initiate ${pct(m.trade_initiation_rate)}% of trades, castle in ${pct(m.castling_rate)}% of games, and develop ${fx(m.development_speed, 1)} minor pieces in the first ten moves.`;
    case 'snake':
      return `You castle in only ${pct(m.castling_rate)}% of games, deploy the queen by move ${r(m.queen_deploy_timing || 12)}, and play ${pct(m.material_imbalance_tolerance)}% of games with unequal material.`;
    default:
      return null;
  }
}

export default function ArchetypePage() {
  const { c } = useDestrierTheme();
  const cache = useDataCache();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [allTimeData, setAllTimeData] = useState(null);
  const [phase, setPhase] = useState('fetching');
  const [, setError] = useState(null);
  const cardCaptureRef = useRef(null);
  const [collapsedDims, setCollapsedDims] = useState({});
  const [cardTimeControl, setCardTimeControl] = useState(null);
  const [cardRevealed, setCardRevealed] = useState(false);
  const [compareGmIdx, setCompareGmIdx] = useState(null); // index into gm_matches_top3
  const [activeLegend, setActiveLegend] = useState(null); // eslint-disable-line no-unused-vars
  const [hoveredLegend, setHoveredLegend] = useState(null); // eslint-disable-line no-unused-vars

  useEffect(() => {
    if (!username) return;
    const ck = `free-report:${username}:${period}`;
    const hit = cache.get(ck);
    if (hit) { setData(hit); setPhase('ready'); return; }
    setPhase('fetching');
    let cancelled = false;
    fetch(`${API_BASE}/free-report?username=${encodeURIComponent(username)}&period=${period}`)
      .then(r => { if (!r.ok) throw new Error('Player not found'); return r.json(); })
      .then(d => { if (!cancelled) { cache.set(ck, d); setData(d); setPhase('ready'); } })
      .catch(e => { if (!cancelled) { setError(e.message); setPhase('error'); } });
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate fetch for all-time data — used for the live ELO on the card so the filter doesn't change it
  useEffect(() => {
    if (!username) return;
    const ck = `free-report:${username}:all`;
    const hit = cache.get(ck);
    if (hit) { setAllTimeData(hit); return; }
    let cancelled = false;
    fetch(`${API_BASE}/free-report?username=${encodeURIComponent(username)}&period=all`)
      .then(r => { if (!r.ok) return null; return r.json(); })
      .then(d => { if (!cancelled && d) { cache.set(ck, d); setAllTimeData(d); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!username) {
    return <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography sx={{ color: c.textMuted }}>Please provide a username.</Typography></Box>;
  }

  const d = data;
  const winPct = d ? Math.round(d.win_rate * 100) : 0;
  const profileKey = d?.archetype?.profile_key;
  const archInfo = d ? ARCHETYPE_INFO[profileKey] || {} : {};
  const heroStatCfg = profileKey ? HERO_STAT_CONFIG[profileKey] : null;
  const heroStatVal = heroStatCfg ? d?.advanced_metrics?.[heroStatCfg.metricKey] : null;
  const heroStatContext = heroStatCfg ? getHeroContext(heroStatCfg.metricKey, heroStatVal) : null;
  const fingerprint = profileKey ? buildFingerprint(profileKey, d?.advanced_metrics) : null;

  // Available time controls — pulled from all-time data so the ELO stays live regardless of the period filter
  const eloSource = allTimeData || d;
  const eloTypes = eloSource?.elo_trajectory?.types || {};
  const availableTCs = Object.keys(eloTypes).filter(k => eloTypes[k]?.games > 0);
  const activeTc = cardTimeControl || eloSource?.elo_trajectory?.main_type || availableTCs[0] || 'rapid';
  const tcData = eloTypes[activeTc] || {};
  const tcElo = tcData.current || eloSource?.current_elo;

  // Dimension data
  const dims = d?.archetype?.dimensions || {};

  // Top openings for card
  const topWhiteOpening = d?.top_white_opening;
  const topBlackOpening = d?.top_black_opening;

  const cellSx = { p: 1.5, borderRadius: '14px', bgcolor: c.mode === 'dark' ? 'rgba(30,30,40,0.6)' : 'rgba(255,255,255,0.4)', border: `1px solid ${c.textMuted}15`, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' };


  // Card content — landing-page style dashboard card
  const cardContent = d && (
    <Box ref={cardCaptureRef} sx={{
      width: '100%', height: '100%',
      p: 3,
      borderRadius: '24px',
      background: c.mode === 'dark'
        ? `linear-gradient(145deg, ${c.cardBg || '#252530'} 0%, #222226 100%)`
        : `linear-gradient(145deg, #f8f2ec 0%, #ECE2DB 100%)`,
      border: '8px solid #2a2a32',
      position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Hero chessboard background */}
      <Box sx={{
        position: 'absolute', inset: -8, opacity: 0.15, filter: 'blur(6px)',
        backgroundImage: "url('/hero-bg.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }} />

      {/* Top accent bar */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 8, background: 'linear-gradient(135deg, #3DB5D4, #2A9AB8)', borderRadius: '24px 24px 0 0' }} />

      {/* Header row: username + archetype (left) / Elo (right) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box>
          <div style={{ fontSize: '2rem', color: '#ECE2DB', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', lineHeight: 1, marginBottom: 2 }}>Chess archetype</div>
          <div style={{ fontSize: '2rem', color: '#8a8a9a', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', lineHeight: 1 }}>@{d.username}</div>
          <div style={{ fontSize: '2rem', color: '#8A74E4', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', marginTop: 2, lineHeight: 1 }}>{singularName(d.archetype?.profile_name)}</div>
          {d.archetype?.tagline && (
            <div style={{ fontSize: '0.85rem', color: '#8a8a9a', fontStyle: 'italic', marginTop: 6, marginBottom: 0, lineHeight: 1 }}>"{d.archetype.tagline}"</div>
          )}
          {archInfo.rarity && (
            <div style={{ marginTop: 10 }}>
              <span style={{ fontSize: '0.95rem', color: ACCENT, background: `${ACCENT}18`, border: `1px solid ${ACCENT}30`, padding: '5px 12px', borderRadius: 8, fontWeight: 700, letterSpacing: '0.04em' }}>
                {archInfo.rarity}% of players
              </span>
            </div>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#ECE2DB', lineHeight: 1 }}>{tcElo}</div>
          <div style={{ fontSize: '0.85rem', color: '#ECE2DB', fontWeight: 600, textTransform: 'uppercase', opacity: 0.6, marginTop: 2 }}>{activeTc} Elo</div>
        </Box>
      </Box>

      {/* Animal image + Radar side by side */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', mb: 1.5, mt: -5, position: 'relative' }}>
        {/* Animal image — left */}
        <Box sx={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 4 }}>
          {ARCHETYPE_IMAGES[d.archetype?.profile_key] && (
            <img src={ARCHETYPE_IMAGES[d.archetype?.profile_key]} alt={singularName(d.archetype?.profile_name)} style={{ width: '100%', height: '100%', objectFit: 'contain', filter: `drop-shadow(0 0 20px ${ACCENT}50)` }} />
          )}
        </Box>

        {/* Strengths Radar — right */}
        {(() => {
          const topStrengths = (d.all_strengths || []).slice(0, 6);
          if (topStrengths.length < 3) return null;
          const sR = 38, sCx = 55, sCy = 55;
          const sAngle = (i) => (Math.PI * 2 * i) / topStrengths.length - Math.PI / 2;
          const sPts = topStrengths.map((s, i) => {
            const a = sAngle(i);
            const v = 0.3 + (s.severity || 0.5) * 0.7;
            return `${sCx + Math.cos(a) * sR * v},${sCy + Math.sin(a) * sR * v}`;
          }).join(' ');
          const sGrid = (scale) => topStrengths.map((_, i) => {
            const a = sAngle(i);
            return `${sCx + Math.cos(a) * sR * scale},${sCy + Math.sin(a) * sR * scale}`;
          }).join(' ');
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, ml: '20px', mt: '-12px' }}>
              <div style={{ fontSize: '0.85rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 0 }}>Core Strengths</div>
            <svg width="200" viewBox="0 0 110 110" fill="none" style={{ overflow: 'visible', marginTop: 40 }}>
              {[0.25, 0.5, 0.75, 1].map((s) => (
                <polygon key={s} points={sGrid(s)} fill="none" stroke={c.textMuted} strokeWidth="0.3" opacity="0.25" />
              ))}
              {topStrengths.map((_, i) => {
                const a = sAngle(i);
                return <line key={i} x1={sCx} y1={sCy} x2={sCx + Math.cos(a) * sR} y2={sCy + Math.sin(a) * sR} stroke={c.textMuted} strokeWidth="0.2" opacity="0.25" />;
              })}
              <polygon points={sPts} fill="#3DB5D420" stroke="#3DB5D4" strokeWidth="1.5" />
              {topStrengths.map((s, i) => {
                const a = sAngle(i);
                const v = 0.3 + (s.severity || 0.5) * 0.7;
                const px = sCx + Math.cos(a) * sR * v;
                const py = sCy + Math.sin(a) * sR * v;
                const lx = sCx + Math.cos(a) * (sR + 12);
                const ly = sCy + Math.sin(a) * (sR + 12);
                return <React.Fragment key={i}>
                  <circle cx={px} cy={py} r="3" fill="#3DB5D4" />
                  {(() => {
                    const words = (s.label || '').split(' ').slice(0, 2);
                    if (words.length > 1) {
                      return <>
                        <text x={lx} y={ly - 4} fill="#ECE2DB" fontSize="6.5" fontWeight="400" textAnchor="middle" dominantBaseline="middle">{words[0]}</text>
                        <text x={lx} y={ly + 4} fill="#ECE2DB" fontSize="6.5" fontWeight="400" textAnchor="middle" dominantBaseline="middle">{words[1]}</text>
                      </>;
                    }
                    return <text x={lx} y={ly} fill="#ECE2DB" fontSize="6.5" fontWeight="400" textAnchor="middle" dominantBaseline="middle">{words[0]}</text>;
                  })()}
                </React.Fragment>;
              })}
            </svg>
            </Box>
          );
        })()}
      </Box>

      {/* 4 Dimensions (left, under animal) + Favorite Openings (right, under radar) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: -6, mb: 1.5, position: 'relative', zIndex: 1, alignItems: 'end' }}>
        <Box sx={{ ...cellSx, p: 1.5 }}>
        <div style={{ fontSize: '0.85rem', color: c.textMuted, fontWeight: 600, marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase' }}>4 Dimensions</div>
        {[
          { l: 'Positional', r: 'Aggressive', v: dims.stance || 0.5, key: 'stance' },
          { l: 'Cleared', r: 'Dense', v: dims.board || 0.5, key: 'board' },
          { l: 'Intense', r: 'Stoic', v: dims.pulse || 0.5, key: 'pulse' },
          { l: 'Specialized', r: 'Diverse', v: dims.arsenal || 0.5, key: 'arsenal' },
        ].map((dim, i) => {
          const pct = Math.round(dim.v * 100);
          return (
            <Box key={i} sx={{ mb: i < 3 ? 1 : 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', mb: 0.3 }}>
                <span style={{ color: dim.v <= 0.5 ? c.textDark : c.textMuted, fontSize: '0.85rem', fontWeight: dim.v <= 0.5 ? 700 : 500 }}>{dim.l}</span>
                <span style={{ color: ACCENT, fontSize: '0.85rem', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: c.mode === 'dark' ? `${ACCENT}12` : `${ACCENT}10`, textAlign: 'center' }}>{pct >= 50 ? pct : 100 - pct}%</span>
                <span style={{ color: dim.v > 0.5 ? c.textDark : c.textMuted, fontSize: '0.85rem', fontWeight: dim.v > 0.5 ? 700 : 500, textAlign: 'right' }}>{dim.r}</span>
              </Box>
              <Box sx={{ height: 5, borderRadius: 3, bgcolor: `${c.textMuted}10`, overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: `${c.textMuted}20` }} />
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, ...(dim.v <= 0.5 ? { right: '50%', width: `${(50 - pct) * 1.3}%` } : { left: '50%', width: `${(pct - 50) * 1.3}%` }), bgcolor: ACCENT, borderRadius: 3 }} />
              </Box>
            </Box>
          );
        })}
        </Box>
        {/* Favorite Openings — right column */}
        <Box sx={{ ...cellSx, p: 2 }}>
          <div style={{ fontSize: '0.85rem', color: c.textMuted, fontWeight: 600, marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Favorite Openings</div>
          {topWhiteOpening && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', backgroundColor: '#ECE2DB', flexShrink: 0 }}></span>
              <span style={{ fontSize: '0.85rem', color: c.textDark, fontWeight: 600, flex: 1 }}>{topWhiteOpening.split(':')[0].split(',')[0].slice(0, 16)}</span>
              {d.white_win_rate != null && <span style={{ fontSize: '0.85rem', color: '#3DB5D4', fontWeight: 700 }}>{Math.round(d.white_win_rate * 100)}% WR</span>}
            </Box>
          )}
          {topBlackOpening && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', backgroundColor: '#111111', flexShrink: 0 }}></span>
              <span style={{ fontSize: '0.85rem', color: c.textDark, fontWeight: 600, flex: 1 }}>{topBlackOpening.split(':')[0].split(',')[0].slice(0, 16)}</span>
              {d.black_win_rate != null && <span style={{ fontSize: '0.85rem', color: '#3DB5D4', fontWeight: 700 }}>{Math.round(d.black_win_rate * 100)}% WR</span>}
            </Box>
          )}
        </Box>
      </Box>

      {/* Fingerprint — full width */}
      {fingerprint && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ ...cellSx, p: 1.5 }}>
            <div style={{ fontSize: '0.85rem', color: c.textMuted, fontWeight: 600, marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Fingerprint</div>
            <div style={{ fontSize: '0.85rem', color: c.textDark, fontWeight: 500, lineHeight: 1.5 }}>{fingerprint}</div>
          </Box>
        </Box>
      )}

      {/* Matchups + GM Match — single row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr', gap: 1.5, mb: 1.5 }}>
        {archInfo.matchups ? (
          <Box sx={{ ...cellSx, p: 1.2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {[
              { label: 'You usually dominate vs', list: archInfo.matchups.dominates, accent: '#3DB5D4' },
              { label: 'You usually struggle vs', list: archInfo.matchups.strugglesVs, accent: '#E4446E' },
            ].map(({ label, list, accent }, rowIdx) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: rowIdx === 0 ? 0.6 : 0 }}>
                <span style={{ fontSize: '0.72rem', color: accent, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.2, width: 92, flexShrink: 0 }}>{label}</span>
                <Box sx={{ display: 'flex', gap: 0.6, flex: 1, minWidth: 0 }}>
                  {list.map((item) => {
                    const k = typeof item === 'string' ? item : item.key;
                    const img = ARCHETYPE_IMAGES[k];
                    const name = ARCHETYPE_NAMES[k] || k;
                    return (
                      <Box key={k} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0,
                        px: 0.7, py: 0.4, borderRadius: '8px',
                        bgcolor: `${accent}12`, border: `1px solid ${accent}25`,
                      }}>
                        {img && <img src={img} alt={name} style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />}
                        <span style={{ fontSize: '0.82rem', color: c.textDark, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        ) : <Box sx={{ ...cellSx, p: 1.2 }} />}
        <Box sx={{ ...cellSx, p: 1.2, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '0.72rem', color: c.textMuted, fontWeight: 600, marginBottom: 2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>GM Match</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 800, color: ACCENT, lineHeight: 1.2, wordBreak: 'break-word' }}>{d.gm_match?.name || '—'}</div>
          {d.gm_match?.similarity != null && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, mt: 0.5, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.3rem', color: '#ECE2DB', fontWeight: 900, lineHeight: 1 }}>{d.gm_match.similarity}%</span>
              <span style={{ fontSize: '0.72rem', color: '#ECE2DB', fontWeight: 600, lineHeight: 1.2 }}>style similarity</span>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer block — Destrier.ai + tagline, centered in remaining space */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, mt: 'auto', mb: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <img src="/logo-light.svg" alt="Destrier" style={{ width: 30, height: 30, transform: 'translateZ(0)', willChange: 'transform' }} />
          <span style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: '1.5rem', color: '#ECE2DB' }}>Destrier<span style={{ color: ACCENT }}>.ai</span></span>
        </Box>
        <span style={{ fontFamily: "'Urbanist', sans-serif", fontSize: '0.85rem', color: '#8a8a9a', letterSpacing: '0.08em', fontWeight: 500 }}>AI-powered chess analytics</span>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      {phase === 'fetching' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid ${c.textMuted}15`, borderTopColor: ACCENT, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Loading your archetype...</Typography>
        </Box>
      )}

      {phase === 'ready' && d && (
        <Box sx={{ px: { xs: 2, md: 3, lg: 4 }, pb: 6, pt: { xs: 9, md: 10 } }}>
          <Box sx={{ position: 'fixed', top: { xs: 56, md: 72 }, left: { xs: 0, md: 0 }, right: { xs: 0, md: '35px' }, display: 'flex', justifyContent: 'center', zIndex: 40, py: 1, pointerEvents: 'none' }}>
            <Box sx={{ pointerEvents: 'auto' }}>
              <TimePeriodFilter value={period} onChange={setPeriod} />
            </Box>
          </Box>

          {/* ── Page Header ── */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
              Your Archetype
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
              Destrier classified your <strong style={{ color: '#8A74E4' }}>chess player DNA</strong> across <strong style={{ color: c.textDark }}>4 dimensions</strong> using <strong style={{ color: c.textDark }}>46 behavioral metrics</strong>.
              This is your chess fingerprint.
            </Typography>
          </Box>

          {/* ── Card (centered) with info flanking ── */}
          <Box sx={{
            display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, gap: 4, mb: 5, alignItems: { xl: 'center' }, justifyContent: 'center',
            '& > *:not(:nth-of-type(2))': {
              filter: cardRevealed ? 'blur(0px)' : 'blur(8px)',
              opacity: cardRevealed ? 1 : 0.4,
              transition: 'filter 0.8s ease, opacity 0.8s ease',
              pointerEvents: cardRevealed ? 'auto' : 'none',
            },
          }}>
            {/* LEFT panel — Archetype description */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, order: { xs: 2, xl: 1 }, justifyContent: 'flex-end' }}>
              <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, position: 'relative', overflow: 'hidden' }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Your Archetype</Typography>
                <Typography sx={{ color: ACCENT, fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 900, mb: 1.5 }}>The {singularName(d.archetype?.profile_name)}</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7, mb: 2 }}>{d.archetype?.description}</Typography>

                {archInfo.playstyle && (
                  <Box sx={{ bgcolor: `${c.textMuted}06`, borderRadius: '12px', p: 2.5, border: `1px solid ${c.textMuted}12` }}>
                    <Typography sx={{ color: ACCENT, fontSize: '1.05rem', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Core Trait</Typography>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700, mb: 0.5 }}>{archInfo.trait}</Typography>
                    <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>{archInfo.playstyle}</Typography>
                  </Box>
                )}
              </Box>

              {/* Tagline quote */}
              {d.archetype?.tagline && (
                <Box sx={{ ...getCardSx(c), p: { xs: 2.5, md: 3 } }}>
                  <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 600, fontStyle: 'italic', lineHeight: 1.6 }}>
                    "{d.archetype.tagline}"
                  </Typography>
                </Box>
              )}
            </Box>

            {/* CENTER: Card + Share */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, order: { xs: 1, xl: 2 }, mx: { xs: 'auto', xl: 0 } }}>
              <Card3D
                front={cardContent}
                back={d && (
                  <Box sx={{
                    width: '100%', height: '100%',
                    borderRadius: '24px',
                    background: '#1a1a20',
                    border: '8px solid #2a2a32',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: 6,
                    position: 'relative', overflow: 'hidden', cursor: 'pointer',
                  }}>
                    {/* Chessboard — 8 columns */}
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      background: 'repeating-conic-gradient(#1a1a20 0% 25%, #212128 0% 50%) 0 0 / 126px 126px',
                      pointerEvents: 'none',
                    }} />
                    {/* Subtle warm light from top-left */}
                    <Box sx={{
                      position: 'absolute', inset: 0, pointerEvents: 'none',
                      background: 'radial-gradient(ellipse at 20% 15%, rgba(138,116,228,0.18) 0%, transparent 50%)',
                    }} />
                    {/* Cool light reflection bottom-right */}
                    <Box sx={{
                      position: 'absolute', inset: 0, pointerEvents: 'none',
                      background: 'radial-gradient(ellipse at 80% 85%, rgba(61,181,212,0.14) 0%, transparent 45%)',
                    }} />
                    {/* Centered content group */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                      <img src="/logo-light.svg" alt="Destrier" style={{ width: 200, height: 200, opacity: 1, marginBottom: -30, position: 'relative' }} />
                      <span style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: '2.4rem', color: '#ECE2DB', position: 'relative' }}>Destrier<span style={{ color: '#8A74E4' }}>.ai</span></span>
                      <Box sx={{ flex: 1, minHeight: 80 }} />
                      {/* Time control selector */}
                      {availableTCs.length > 1 && (
                        <>
                          <span style={{ fontFamily: "'Urbanist', sans-serif", fontSize: '1.2rem', fontWeight: 700, color: '#ECE2DB', letterSpacing: '0.03em', marginBottom: 12 }}>
                            {cardTimeControl ? 'Game mode' : 'Select your game mode'}
                          </span>
                          <Box sx={{ display: 'flex', gap: 1, mb: 3, position: 'relative' }}>
                            {availableTCs.map((tc) => (
                              <Box key={tc} onClick={(e) => { e.stopPropagation(); setCardTimeControl(tc); }} sx={{
                                px: 2.5, py: 0.8, borderRadius: '50px', cursor: 'pointer',
                                bgcolor: cardTimeControl === tc ? '#8A74E420' : 'rgba(26,26,32,0.4)',
                                border: `2px solid ${cardTimeControl === tc ? '#8A74E4' : '#ECE2DB'}${cardTimeControl === tc ? '60' : '40'}`,
                                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                                transition: 'all 0.15s ease',
                                '&:hover': { bgcolor: '#8A74E415' },
                              }}>
                                <span style={{ fontSize: '1.1rem', fontWeight: cardTimeControl === tc ? 700 : 500, color: cardTimeControl === tc ? '#ECE2DB' : '#ECE2DB80', textTransform: 'capitalize', fontFamily: "'Urbanist', sans-serif", letterSpacing: '0.03em' }}>{tc}</span>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                      <Box
                        onClick={cardTimeControl || availableTCs.length <= 1 ? undefined : (e) => e.stopPropagation()}
                        sx={{
                          px: 5, py: 2, borderRadius: '50px',
                          border: `3px solid ${cardTimeControl || availableTCs.length <= 1 ? '#8A74E460' : '#ECE2DB15'}`,
                          background: cardTimeControl || availableTCs.length <= 1 ? '#8A74E410' : 'rgba(26,26,32,0.4)',
                          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                          position: 'relative',
                          transition: 'all 0.25s ease',
                          opacity: cardTimeControl || availableTCs.length <= 1 ? 1 : 0.3,
                          cursor: cardTimeControl || availableTCs.length <= 1 ? 'pointer' : 'default',
                          ...((cardTimeControl || availableTCs.length <= 1) && {
                            '&:hover': { background: '#8A74E420', border: '3px solid #8A74E480', transform: 'scale(1.04)', boxShadow: '0 0 24px #8A74E415' },
                          }),
                        }}
                      >
                        <span style={{ fontFamily: "'Urbanist', sans-serif", fontSize: '1.4rem', fontWeight: 700, color: '#ECE2DB', letterSpacing: '0.03em' }}>
                          Reveal my Archetype
                        </span>
                      </Box>
                      <span style={{ color: '#ECE2DB', opacity: cardTimeControl || availableTCs.length <= 1 ? 1 : 0.3, fontSize: '1.2rem', fontWeight: 700, marginTop: 14, fontFamily: "'Urbanist', sans-serif", letterSpacing: '0.03em', transition: 'opacity 0.2s ease' }}>
                        {cardTimeControl || availableTCs.length <= 1 ? 'Click to flip' : 'Select a mode first'}
                      </span>
                    </Box>
                  </Box>
                )}
                width={520}
                height={880}
                flipped={!cardRevealed}
                onFlip={() => { if (cardTimeControl || availableTCs.length <= 1) setCardRevealed(r => !r); }}
              />
            </Box>

            {/* RIGHT panel — GM Match */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, order: { xs: 3, xl: 3 }, justifyContent: 'flex-end' }}>
              {d.gm_match && (
                <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 } }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>GM Match</Typography>
                  <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.8rem' }, fontWeight: 900, mb: 1 }}>You play like {d.gm_match.name}</Typography>
                  <Box sx={{ display: 'inline-flex', bgcolor: `${ACCENT}10`, border: `1px solid ${ACCENT}20`, borderRadius: '10px', px: 2, py: 0.5, mb: 2 }}>
                    <Typography sx={{ color: ACCENT, fontSize: '1.05rem', fontWeight: 800 }}>{d.gm_match.similarity}% match</Typography>
                  </Box>
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
                    {d.gm_match.description}
                    {' '}Study {d.gm_match.name}'s games to find ideas that naturally fit your style.
                  </Typography>
                </Box>
              )}

              {/* Quick stats */}
              <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 } }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 2 }}>Quick Stats</Typography>
                {[
                  { label: 'Games Analyzed', value: d.total_games?.toLocaleString(), color: '#DE7345' },
                  { label: 'Win Rate', value: `${winPct}%`, color: '#00A5C4' },
                  { label: `Current Elo (${activeTc})`, value: tcElo, color: ACCENT },
                  { label: 'Arsenal', value: d.archetype?.arsenal || '—', color: ACCENT },
                ].map((stat, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.2, borderBottom: i < 3 ? `1px solid ${c.textMuted}10` : 'none' }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>{stat.label}</Typography>
                    <Typography sx={{ color: stat.color, fontSize: '1.1rem', fontWeight: 800 }}>{stat.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* ── Sections below card — blurred until revealed ── */}
          <Box sx={{
            filter: cardRevealed ? 'blur(0px)' : 'blur(8px)',
            opacity: cardRevealed ? 1 : 0.4,
            transition: 'filter 0.8s ease, opacity 0.8s ease',
            pointerEvents: cardRevealed ? 'auto' : 'none',
          }}>

          {/* ── Rarity ── */}
          {archInfo.rarity && (
            <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, mb: 5, display: 'flex', alignItems: 'center', gap: 3, border: `1px solid ${ACCENT}20` }}>
              <Box sx={{ flexShrink: 0, width: 64, height: 64, borderRadius: '50%', bgcolor: `${ACCENT}10`, border: `2px solid ${ACCENT}25`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: ACCENT, fontSize: '1.4rem', fontWeight: 900 }}>{archInfo.rarity}%</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 800, mb: 0.3 }}>
                  Only {archInfo.rarity}% of players are {singularName(d.archetype?.profile_name)}s
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
                  Based on Destrier's classification of players across the 4 personality dimensions.
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── Signature stat + Behavioral fingerprint ── */}
          {(heroStatCfg && heroStatVal != null) || fingerprint ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.6fr' }, gap: 3, mb: 5 }}>
              {/* Signature stat */}
              {heroStatCfg && heroStatVal != null && (
                <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, border: `1.5px solid ${ACCENT}30`, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
                    Your Signature Stat
                  </Typography>
                  <Typography sx={{ color: ACCENT, fontSize: { xs: '2.4rem', md: '3rem' }, fontWeight: 900, lineHeight: 1 }}>
                    {heroStatCfg.format(heroStatVal)}
                  </Typography>
                  <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700, mt: 1 }}>
                    {heroStatCfg.label}
                  </Typography>
                  {heroStatContext && (
                    <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mt: 0.4, fontStyle: 'italic' }}>
                      {heroStatContext}
                    </Typography>
                  )}
                </Box>
              )}
              {/* Behavioral fingerprint */}
              {fingerprint && (
                <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, border: `1px solid ${ACCENT}20`, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.5 }}>
                    Behavioral Fingerprint
                  </Typography>
                  <Typography sx={{ color: c.textDark, fontSize: { xs: '1.1rem', md: '1.2rem' }, fontWeight: 500, lineHeight: 1.6, fontStyle: 'italic' }}>
                    "{fingerprint}"
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}

          {/* ── Strengths & Weaknesses ── */}
          {archInfo.strengths && archInfo.weaknesses && (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Strengths & Weaknesses</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                Every archetype has natural advantages and blind spots. Knowing yours is the first step to leveraging the strengths and patching the gaps.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Strengths */}
                <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, border: `1px solid #3DB5D420` }}>
                  <Typography sx={{ color: '#3DB5D4', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 2 }}>Strengths</Typography>
                  {archInfo.strengths.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: i < archInfo.strengths.length - 1 ? 2 : 0 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '6px', bgcolor: '#3DB5D410', border: '1px solid #3DB5D420', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3DB5D4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </Box>
                      <Typography sx={{ color: c.textDark, fontSize: '1.05rem', lineHeight: 1.6 }}>{s}</Typography>
                    </Box>
                  ))}
                </Box>
                {/* Weaknesses */}
                <Box sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, border: `1px solid #E4446E20` }}>
                  <Typography sx={{ color: '#E4446E', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 2 }}>Weaknesses</Typography>
                  {archInfo.weaknesses.map((w, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: i < archInfo.weaknesses.length - 1 ? 2 : 0 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '6px', bgcolor: '#E4446E10', border: '1px solid #E4446E20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#E4446E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </Box>
                      <Typography sx={{ color: c.textDark, fontSize: '1.05rem', lineHeight: 1.6 }}>{w}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* ── Matchups ── */}
          {archInfo.matchups && (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>How You Match Up</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                Based on your playing style, here are the archetypes you tend to dominate — and the ones that give you trouble. Each pairing is explained below.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {[
                  { title: 'You usually dominate vs.', list: archInfo.matchups.dominates, accent: '#3DB5D4' },
                  { title: 'You usually struggle vs.', list: archInfo.matchups.strugglesVs, accent: '#E4446E' },
                ].map(({ title, list, accent }) => (
                  <Box key={title} sx={{ ...getCardSx(c), p: { xs: 3, md: 4 }, border: `1px solid ${accent}30` }}>
                    <Typography sx={{ color: accent, fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 4 }}>{title}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {list.map((item) => {
                        const k = item.key;
                        const img = ARCHETYPE_IMAGES[k];
                        const name = ARCHETYPE_NAMES[k] || k;
                        return (
                          <Box key={k} sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1.5,
                            p: { xs: 1.5, md: 1.75 }, borderRadius: '12px',
                            bgcolor: `${accent}0A`, border: `1px solid ${accent}22`,
                          }}>
                            <Box sx={{
                              flexShrink: 0, width: 42, height: 42, borderRadius: '10px',
                              bgcolor: `${accent}12`, border: `1px solid ${accent}25`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {img && <img src={img} alt={name} style={{ width: 32, height: 32, objectFit: 'contain' }} />}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 800, mb: 0.3 }}>{name}</Typography>
                              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.5 }}>{item.reason}</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Players Like You (user tile + GM tiles) ── */}
          {d.gm_matches_top3 && d.gm_matches_top3.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Players Like You</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                These grandmasters match your playing style the closest — compare their dimensions to yours.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr repeat(3, 1fr)' }, gap: 2.5 }}>

                {/* ── User tile ── */}
                <Box sx={{
                  ...getCardSx(c), p: { xs: 2.5, md: 3 }, overflow: 'hidden',
                  border: `1px solid ${USER_COLOR}20`,
                }}>
                  {/* User header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, minHeight: { md: 80 } }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: `${USER_COLOR}10`, border: `1.5px solid ${USER_COLOR}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={USER_COLOR} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M20 21a8 8 0 10-16 0" />
                      </svg>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: USER_COLOR, fontSize: { xs: '1.3rem', md: '1.4rem' }, fontWeight: 800 }}>You</Typography>
                      <Typography sx={{ color: c.textMuted, fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.5 }}>{d.username}</Typography>
                    </Box>
                  </Box>

                  {/* User dimensions */}
                  {['stance', 'board', 'pulse', 'arsenal'].map((dimKey) => {
                    const def = DIMENSION_METRICS[dimKey];
                    const val = d.archetype?.dimensions?.[dimKey] || 0;
                    const pct = Math.round(val * 100);
                    const dominant = pct > 50 ? def.right : def.left;
                    return (
                      <Box key={dimKey} sx={{ mb: dimKey !== 'arsenal' ? 2 : 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{def.label}</Typography>
                          <Typography sx={{ color: USER_COLOR, fontSize: '1.05rem', fontWeight: 800 }}>{dominant}</Typography>
                        </Box>
                        <Box sx={{ px: 1.5, mt: 3.5 }}>
                          <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: `${c.textMuted}10`, overflow: 'visible' }}>
                            <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: `${c.textMuted}20`, zIndex: 1 }} />
                            <Box sx={{
                              position: 'absolute', top: 0, bottom: 0,
                              ...(pct <= 50 ? { right: '50%', width: `${50 - pct}%` } : { left: '50%', width: `${pct - 50}%` }),
                              bgcolor: USER_COLOR, borderRadius: 4, opacity: 0.5,
                            }} />
                            {/* Dot with hover + percentage */}
                            <Box sx={{
                              position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                              width: 22, height: 22, borderRadius: '50%', bgcolor: USER_COLOR,
                              border: `3px solid ${c.cardBg || c.bg}`, boxShadow: `0 0 10px ${USER_COLOR}40`, zIndex: 2,
                              cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                              '&:hover': { transform: 'translate(-50%, -50%) scale(1.18)', boxShadow: `0 0 14px ${USER_COLOR}80` },
                              '&:hover .pct-label': { transform: 'translateX(-50%) scale(1.18)' },
                            }}>
                              <Typography className="pct-label" sx={{ color: USER_COLOR, fontSize: '0.92rem', fontWeight: 900, position: 'absolute', left: '50%', top: -26, transform: 'translateX(-50%)', transition: 'transform 0.2s ease', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{Math.max(pct, 100 - pct)}%</Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontStyle: 'italic' }}>{def.left}</Typography>
                          <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontStyle: 'italic' }}>{def.right}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* ── GM tiles ── */}
                {d.gm_matches_top3.map((gm, idx) => {
                  const gmDims = gm.dimensions || {};
                  const gmColor = GM_COLORS[idx] || GM_COLORS[0];
                  return (
                    <Box key={gm.name} sx={{
                      ...getCardSx(c), p: { xs: 2.5, md: 3 }, overflow: 'hidden',
                      border: `1px solid ${gmColor}30`,
                      '&:hover': { borderColor: `${gmColor}50`, transform: 'translateY(-2px)' },
                      transition: 'all 0.2s ease',
                    }}>
                      {/* GM header */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, minHeight: { md: 80 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: `${gmColor}10`, border: `1.5px solid ${gmColor}25`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={gmColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 3a3 3 0 00-3 3v1H9V6a3 3 0 00-6 0v7c0 5 3.5 8 9 8s9-3 9-8V6a3 3 0 00-3-3z" />
                            </svg>
                          </Box>
                          <Typography sx={{ color: gmColor, fontSize: '1.15rem', fontWeight: 900, lineHeight: 1 }}>{gm.similarity}%</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: gmColor, fontSize: { xs: '1.3rem', md: '1.4rem' }, fontWeight: 800 }}>
                            {gm.name}
                            {gmFlag(gm.name) && <span style={{ marginLeft: 8 }}>{gmFlag(gm.name)}</span>}
                          </Typography>
                          <Typography sx={{ color: c.textMuted, fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.5 }}>{gm.description}</Typography>
                        </Box>
                      </Box>

                      {/* GM dimensions only */}
                      {['stance', 'board', 'pulse', 'arsenal'].map((dimKey) => {
                        const def = DIMENSION_METRICS[dimKey];
                        const gmVal = gmDims[dimKey] || 0;
                        const gmPct = Math.round(gmVal * 100);
                        const dominant = gmPct > 50 ? def.right : def.left;
                        return (
                          <Box key={dimKey} sx={{ mb: dimKey !== 'arsenal' ? 2 : 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{def.label}</Typography>
                              <Typography sx={{ color: gmColor, fontSize: '1.05rem', fontWeight: 800 }}>{dominant}</Typography>
                            </Box>
                            <Box sx={{ px: 1.5, mt: 3.5 }}>
                              <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: `${c.textMuted}10`, overflow: 'visible' }}>
                                <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: `${c.textMuted}20`, zIndex: 1 }} />
                                <Box sx={{
                                  position: 'absolute', top: 0, bottom: 0,
                                  ...(gmPct <= 50 ? { right: '50%', width: `${50 - gmPct}%` } : { left: '50%', width: `${gmPct - 50}%` }),
                                  bgcolor: gmColor, borderRadius: 4, opacity: 0.4,
                                }} />
                                {/* Dot with hover + percentage */}
                                <Box sx={{
                                  position: 'absolute', left: `${gmPct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                                  width: 22, height: 22, borderRadius: '50%', bgcolor: gmColor,
                                  border: `3px solid ${c.cardBg || c.bg}`, boxShadow: `0 0 10px ${gmColor}60`, zIndex: 2,
                                  cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                  '&:hover': { transform: 'translate(-50%, -50%) scale(1.18)', boxShadow: `0 0 14px ${gmColor}90` },
                                  '&:hover .pct-label': { transform: 'translateX(-50%) scale(1.18)' },
                                }}>
                                  <Typography className="pct-label" sx={{ color: gmColor, fontSize: '0.92rem', fontWeight: 900, position: 'absolute', left: '50%', top: -26, transform: 'translateX(-50%)', transition: 'transform 0.2s ease', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{Math.max(gmPct, 100 - gmPct)}%</Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontStyle: 'italic' }}>{def.left}</Typography>
                              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontStyle: 'italic' }}>{def.right}</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── 4 Dimensions (4-column layout) ── */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Your 4 Dimensions</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, maxWidth: 700 }}>
                These four axes define how you approach the board. Select a GM to compare, then click any dimension to see the raw metrics.
              </Typography>
            </Box>

            {/* GM compare selector — centered */}
            {d.gm_matches_top3 && d.gm_matches_top3.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 3 }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600 }}>Compare to:</Typography>
                <Box sx={{ display: 'flex', gap: 0.8 }}>
                  {d.gm_matches_top3.map((gm, idx) => {
                    const gmColor = GM_COLORS[idx] || GM_COLORS[0];
                    const isActive = compareGmIdx === idx;
                    return (
                      <Box
                        key={gm.name}
                        onClick={() => setCompareGmIdx(prev => prev === idx ? null : idx)}
                        sx={{
                          px: 2, py: 0.7, borderRadius: '10px', cursor: 'pointer',
                          bgcolor: isActive ? `${gmColor}20` : `${c.textMuted}08`,
                          border: `1.5px solid ${isActive ? gmColor : c.textMuted}${isActive ? '' : '20'}`,
                          '&:hover': { borderColor: gmColor, bgcolor: `${gmColor}10` },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Typography sx={{ color: isActive ? gmColor : c.textMuted, fontSize: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {gm.name.split(' ').pop()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {(() => {
              const compareGm = compareGmIdx != null ? d.gm_matches_top3?.[compareGmIdx] : null;
              const compareColor = compareGmIdx != null ? (GM_COLORS[compareGmIdx] || GM_COLORS[0]) : c.textMuted;
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2, alignItems: 'start' }}>
                  {d.archetype?.dimensions && Object.entries(DIMENSION_METRICS).map(([dimKey, dimDef]) => {
                    const dimValue = d.archetype.dimensions[dimKey];
                    const userPct = Math.round(dimValue * 100);
                    const gmDimValue = compareGm?.dimensions?.[dimKey];
                    const gmPct = gmDimValue != null ? Math.round(gmDimValue * 100) : null;
                    const isExpanded = !collapsedDims[dimKey];

                    return (
                      <Box key={dimKey} sx={{
                        ...getCardSx(c), overflow: 'hidden', display: 'flex', flexDirection: 'column',
                        border: `1px solid ${isExpanded ? ACCENT : c.textMuted}${isExpanded ? '30' : '10'}`,
                        transition: 'border-color 0.3s ease',
                      }}>
                        {/* Dimension header */}
                        <Box
                          onClick={() => setCollapsedDims(prev => ({ ...prev, [dimKey]: !prev[dimKey] }))}
                          sx={{ p: { xs: 2, md: 2.5 }, cursor: 'pointer', '&:hover': { bgcolor: `${ACCENT}06` }, transition: 'background 0.2s ease' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography sx={{ color: ACCENT, fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{dimDef.label}</Typography>
                            {d.archetype.dimension_labels?.[dimKey] && (
                              <Box sx={{ bgcolor: `${ACCENT}12`, borderRadius: '6px', px: 0.8, py: 0.1 }}>
                                <Typography sx={{ color: ACCENT, fontSize: '0.88rem', fontWeight: 700 }}>{d.archetype.dimension_labels[dimKey]}</Typography>
                              </Box>
                            )}
                            <Box sx={{ ml: 'auto', color: c.textMuted, transition: 'transform 0.3s ease', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </Box>
                          </Box>

                          {/* Single combined bar with user dot + optional GM dot */}
                          <Box sx={{ mb: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography sx={{ color: userPct <= 50 ? c.textDark : c.textMuted, fontSize: '1.05rem', fontWeight: userPct <= 50 ? 700 : 500 }}>{dimDef.left}</Typography>
                              <Typography sx={{ color: userPct > 50 ? c.textDark : c.textMuted, fontSize: '1.05rem', fontWeight: userPct > 50 ? 700 : 500 }}>{dimDef.right}</Typography>
                            </Box>
                            <Box sx={{ px: 1.5, mt: 4 }}>
                              <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: `${c.textMuted}10`, overflow: 'visible' }}>
                                <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: `${c.textMuted}20`, zIndex: 1 }} />
                                {/* Colored fill from center */}
                                <Box sx={{
                                  position: 'absolute', top: 0, bottom: 0,
                                  ...(userPct <= 50 ? { right: '50%', width: `${50 - userPct}%` } : { left: '50%', width: `${userPct - 50}%` }),
                                  bgcolor: USER_COLOR, borderRadius: 4, transition: 'width 1.2s cubic-bezier(0.16, 1, 0.3, 1)', opacity: 0.6,
                                }} />
                                {/* User percentage above dot */}
                                <Typography sx={{ color: USER_COLOR, fontSize: '1.25rem', fontWeight: 900, position: 'absolute', left: `${userPct}%`, top: -38, transform: 'translateX(-50%)', zIndex: 3 }}>{Math.max(userPct, 100 - userPct)}%</Typography>
                                {/* User dot */}
                                <Box sx={{ position: 'absolute', left: `${userPct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: '50%', bgcolor: USER_COLOR, border: `3px solid ${c.cardBg || c.bg}`, boxShadow: `0 0 10px ${USER_COLOR}40`, zIndex: 3 }} />
                                {/* GM dot (if selected) */}
                                {gmPct != null && (
                                  <>
                                    <Typography sx={{ color: compareColor, fontSize: '1.1rem', fontWeight: 900, position: 'absolute', left: `${gmPct}%`, top: 22, transform: 'translateX(-50%)', zIndex: 3, whiteSpace: 'nowrap' }}>{Math.max(gmPct, 100 - gmPct)}%</Typography>
                                    <Box sx={{ position: 'absolute', left: `${gmPct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: '50%', bgcolor: compareColor, border: `3px solid ${c.cardBg || c.bg}`, boxShadow: `0 0 10px ${compareColor}60`, zIndex: 2 }} />
                                  </>
                                )}
                              </Box>
                            </Box>
                            {/* Bottom labels + legend when comparing */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: gmPct != null ? 3.5 : 0.8 }}>
                              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontStyle: 'italic' }}>{dimDef.desc}</Typography>
                            </Box>
                            {compareGm && (
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: USER_COLOR }} />
                                  <Typography sx={{ color: USER_COLOR, fontSize: '0.88rem', fontWeight: 700 }}>You</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: compareColor }} />
                                  <Typography sx={{ color: compareColor, fontSize: '0.88rem', fontWeight: 700 }}>{compareGm.name.split(' ').pop()}</Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Expanded metrics */}
                        {isExpanded && d.advanced_metrics && (
                          <Box sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5, borderTop: `1px solid ${c.textMuted}10`, flex: 1 }}>
                            <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, mt: 1.5, mb: 1.5 }}>
                              Raw metrics:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {dimDef.metrics.map((metric) => {
                                const rawValue = d.advanced_metrics[metric.key];
                                if (rawValue == null) return null;
                                const gmValue = compareGm?.metrics?.[metric.key];
                                // Compute delta indicator when comparing to a GM
                                let deltaEl = null;
                                if (compareGm && gmValue != null) {
                                  const diff = rawValue - gmValue;
                                  const pctDiff = gmValue !== 0 ? Math.abs(diff / gmValue) : (diff !== 0 ? 1 : 0);
                                  // Thresholds: <5% = equal, 5-20% = slight, >20% = big
                                  if (pctDiff < 0.05) {
                                    deltaEl = (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: `${c.textMuted}10`, borderRadius: '6px', px: 1, py: 0.3 }}>
                                        <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontWeight: 700 }}>=</Typography>
                                        <Typography sx={{ color: c.textMuted, fontSize: '0.82rem', fontWeight: 600 }}>Similar</Typography>
                                      </Box>
                                    );
                                  } else {
                                    const isHigher = diff > 0;
                                    const intensity = pctDiff > 0.2;
                                    const arrowColor = isHigher ? '#3DB5D4' : '#E4446E';
                                    const arrow = isHigher ? '\u25B2' : '\u25BC';
                                    const label = isHigher
                                      ? (intensity ? 'Much higher' : 'Higher')
                                      : (intensity ? 'Much lower' : 'Lower');
                                    deltaEl = (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: `${arrowColor}10`, borderRadius: '6px', px: 1, py: 0.3 }}>
                                        <Typography sx={{ color: arrowColor, fontSize: '0.82rem', fontWeight: 800 }}>{arrow}</Typography>
                                        <Typography sx={{ color: arrowColor, fontSize: '0.82rem', fontWeight: 600 }}>{label}</Typography>
                                      </Box>
                                    );
                                  }
                                }
                                return (
                                  <Box key={metric.key} sx={{
                                    p: 2, borderRadius: '12px',
                                    bgcolor: c.mode === 'dark' ? `${c.textMuted}06` : `${c.textMuted}04`,
                                    border: `1px solid ${c.textMuted}10`,
                                  }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600 }}>{metric.label}</Typography>
                                      {deltaEl}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                                      <Typography sx={{ color: USER_COLOR, fontSize: '1.25rem', fontWeight: 800 }}>{metric.format(rawValue)}</Typography>
                                      {compareGm && gmValue != null && (
                                        <>
                                          <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', opacity: 0.5 }}>vs</Typography>
                                          <Typography sx={{ color: compareColor, fontSize: '1.25rem', fontWeight: 800 }}>{metric.format(gmValue)}</Typography>
                                        </>
                                      )}
                                    </Box>
                                    {metric.desc && (
                                      <Typography sx={{ color: c.textMuted, fontSize: '1rem', lineHeight: 1.5, mt: 1 }}>
                                        {metric.desc}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Box>


          </Box>{/* end blur wrapper */}
        </Box>
      )}
    </Box>
  );
}
